import React from "react";
import { arrayUtil, SafetyInspection } from "../../../amorg-common";
import { Card } from "../../../designSystem/Card";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { FormField } from "../../../designSystem/formField/FormField";
import { SegmentBar } from "../../../designSystem/graphs/SegmentBar";
import { Icon } from '../../../designSystem/icons/Icons';
import { getFacilityTypeIcon } from '../../../utils/facilityHelper';
import { StateDetails } from "../../../pages/InspectionPointPage";
import { InstructionText } from "./InstructionText";
import { Label } from "../../../designSystem/Label";
import { ResponsiveColumns } from "../../../designSystem/ResponsiveColumns";
import { colors } from "../../../designSystem/colors/color";
import { Button } from "../../../designSystem/button/Button";
import { Redirect } from "react-router-dom";

type InspectionOverviewProps = {
    activityId:string,
    safetyInspection:SafetyInspection,
    details:StateDetails,
    onRedirect:(url:string)=>void,
}
export function InspectionOverview({activityId, safetyInspection, details, onRedirect}:InspectionOverviewProps):JSX.Element
{
    const [redirect, setRedirect] = React.useState<string>("");

    const flatData = safetyInspection.locations.flatMap(l => l.facilities.flatMap(f => f.inspectionPoints.flatMap(ip => ({ locationId:l.locationId, facilityId:f.facilityId, inspection:ip }))));
    const flatDataIndex = flatData.findIndex(x => x.inspection.inspectionResult.id === details.inspection.inspectionResult.id);
    const previousFlatData = flatData.slice(0, flatDataIndex);
    const nextFlatData = flatData.slice(flatDataIndex+1);

    function withOptionalCountText(text:string, index:number, total:number):string
    {
        if(total === 1)
            return text;
        return `${text} (${index+1} ud af ${total})`;
    }

    function getLocationRedirect(dir:'back'|'forward')
    {
        // back <- first inspection of last locations first facility
        // forward -> first inspection of next locations first facility
        if(dir === 'back')
        {
            const previousLocation = arrayUtil.lastWhere(previousFlatData, (fd) => fd.locationId !== details.location.locationId);
            if(previousLocation !== undefined){
                const firstFacility = arrayUtil.firstWhere(previousFlatData, (fd) => fd.locationId === previousLocation.locationId);
                const firstInspectionPoint = firstFacility.inspection;
                return () => setRedirect(`/safetyinspections/${activityId}/inspections/${firstInspectionPoint.inspectionResult.id}`);
            }
        }
        else if(dir === 'forward')
        {
            const nextLocation = arrayUtil.firstWhere(nextFlatData, (fd) => fd.locationId !== details.location.locationId);
            if(nextLocation !== undefined){
                const firstInspectionPoint = nextLocation.inspection;
                return () => setRedirect(`/safetyinspections/${activityId}/inspections/${firstInspectionPoint.inspectionResult.id}`);
            }
        }
        return undefined;
    }

    function getFacilityRedirect(dir:'back'|'forward')
    {
        // back <- first inspection of last facility
        // forward -> first inspection of next facility
        if(dir === 'back')
        {
            const previousFacility = arrayUtil.lastWhere(previousFlatData, (fd) => fd.facilityId !== details.facility.facilityId);
            if(previousFacility !== undefined){
                const firstInspectionPoint = arrayUtil.firstWhere(previousFlatData, (fd) => fd.facilityId === previousFacility.facilityId).inspection;
                return () => setRedirect(`/safetyinspections/${activityId}/inspections/${firstInspectionPoint.inspectionResult.id}`);
            }
        }
        else if(dir === 'forward')
        {
            const nextFacility = arrayUtil.firstWhere(nextFlatData, (fd) => fd.facilityId !== details.facility.facilityId);
            if(nextFacility !== undefined){
                const firstInspectionPoint = arrayUtil.firstWhere(nextFlatData, (fd) => fd.facilityId === nextFacility.facilityId).inspection;
                return () => setRedirect(`/safetyinspections/${activityId}/inspections/${firstInspectionPoint.inspectionResult.id}`);
            }
        }
        return undefined;
    }

    function getInspectionRedirect(dir:'back'|'forward')
    {
        if(dir === 'back')
        {
            if(flatDataIndex > 0){
                return () => setRedirect(`/safetyinspections/${activityId}/inspections/${flatData[flatDataIndex-1].inspection.inspectionResult.id}`);
            }
        }
        else if(dir === 'forward')
        {
            if(flatDataIndex < flatData.length-1){
                return () => setRedirect(`/safetyinspections/${activityId}/inspections/${flatData[flatDataIndex+1].inspection.inspectionResult.id}`);
            }
        }
        return undefined;
    }

    return (
        <Flex flexDirection='column' gap={32}>
            {redirect && <Redirect to={redirect} />}
            <div>
                <SegmentBar
                    gapPx={1}
                    totalSegments={details.allInspections.length}
                    selectedIndex={details.currentTotalIndex}
                    height={30}
                    selectionPxIncrease={10}
                    segments={details.allInspections.map((x) =>
                        ({
                            color:  x.inspectionResult.status === 'pending' ? colors.graphs.progress.pending 
                                    : x.inspectionResult.status === 'passed' ? colors.graphs.progress.success
                                    : colors.graphs.progress.error,
                            onClick: () => onRedirect(`/safetyinspections/${activityId}/inspections/${x.inspectionResult.id}`)
                        })
                    )}
                />
                <div style={{textAlign:'center', fontWeight:'600'}}>
                    {details.allInspections.filter(x => x.inspectionResult.status !== 'pending').length} ud af {details.allInspections.length} kontrolpunkter gennemført
                </div>
            </div>
            
            <Card>
                <Flex flexDirection='column' gap={16}>

                    <ResponsiveColumns columnCount={2} tabletColumnCount={1} mobileColumnCount={1} rowGap={20} columnGap={20}>
                    
                        <Flex alignItems="flex-end">
                            <Icon name={'facility_domain'} />                      
                            <Flex alignItems="flex-end" gap={0}>
                                <Button icon="caret_caretBack" variant="text" heightOverride="22px" disabled={getLocationRedirect('back') === undefined}
                                    onClick={getLocationRedirect('back')} />  
                                <Flex flexDirection="column" alignItems='flex-start'>
                                    <Label>Lokation</Label>
                                    <span>{withOptionalCountText(safetyInspection.locations[details.locationIndex].name, details.locationIndex, safetyInspection.locations.length)}</span> 
                                </Flex>
                                <Button icon="caret_caretForward" variant="text"heightOverride="22px" disabled={getLocationRedirect('forward') === undefined}
                                    onClick={getLocationRedirect('forward')} />
                            </Flex>
                        </Flex>

                        <Flex alignItems="flex-end">
                            <Icon name={getFacilityTypeIcon(details.facility.facilityType.id)} />
                            <Flex alignItems="flex-end" gap={0}>
                                <Button icon="caret_caretBack" variant="text" heightOverride="22px" disabled={getFacilityRedirect('back') === undefined}
                                    onClick={getFacilityRedirect('back')} />  
                                <Flex flexDirection='column' alignItems='flex-start'>
                                    <Label>Facilitet</Label>
                                    <span>{withOptionalCountText(details.facility.name, details.facilityIndex, details.location.facilities.length)}</span>
                                </Flex>
                                <Button icon="caret_caretForward" variant="text"heightOverride="22px" disabled={getFacilityRedirect('forward') === undefined}
                                    onClick={getFacilityRedirect('forward')} />
                            </Flex>
                        </Flex>
                    </ResponsiveColumns>

                    <Flex alignItems="flex-end">
                        <Icon name="fact_check" />
                        <Flex alignItems="flex-end" gap={0}>
                            <Button icon="caret_caretBack" variant="text" heightOverride="22px" disabled={getInspectionRedirect('back') === undefined}
                                    onClick={getInspectionRedirect('back')} />  
                            <Flex flexDirection='column' alignItems='flex-start'>
                                <Label>Kontrolpunkt</Label>
                                <span>{withOptionalCountText(details.inspection.inspectionPoint.title, details.inspectionIndex, details.facility.inspectionPoints.length)}</span>
                            </Flex>
                            <Button icon="caret_caretForward" variant="text"heightOverride="22px" disabled={getInspectionRedirect('forward') === undefined}
                                    onClick={getInspectionRedirect('forward')} />
                        </Flex>
                    </Flex>

                    <FormField
                        label="Vejledning"
                        inputComponent={
                            <Card variant="outlined">
                                <InstructionText text={details.inspection.inspectionPoint.instructions} defaultCollapsed={true} />
                            </Card>
                        } />

                </Flex>
            </Card>
        </Flex>
    );
}